import {
  ADDRESS_FIELD_ID,
  BUSINESS_FIELD_ID,
  CLASS_FIELD_ID,
  isW9SystemId,
  NAME_FIELD_ID,
  TIN_FIELD_ID,
  W9_EXPORT_EXCLUDED,
} from '../../../W9/shared/w9Template';
import { isLayoutType } from '../../shared/fieldTypes';
import {
  META_KEYS,
  META_LABELS,
  columnHeader,
  columnsById,
  isMetaKey,
  orderedColumns,
} from '../exportMatrix';

// Saved table views (design sub_columns.jsx, Implementation List §5). A
// view is { id, name, cols: [key], builtIn? }; keys are exportMatrix keys
// (_id, _date, _by, _email) or real field ids. Views and the last export
// choice are stored per form and per signed-in user in this browser.
//
// A W-9 form (w9-plan §6.3 item 7) seeds three built-in views
// (w9BuiltInViews) and its export catalog leaves out the TIN and the
// signature (catalogOf's `exclude`, W9_EXPORT_EXCLUDED). Both are opt-in;
// Forms calls everything as before.

export const DEFAULT_VIEW_ID = 'default';

// ---- columns -------------------------------------------------------------

// Grid width per answer type (design widthFor).
const WIDTH_BY_TYPE = {
  name: 170,
  email: 200,
  phone: 140,
  address: 240,
  long_answer: 240,
  number: 90,
  datetime: 120,
  repeater: 110,
  table: 110,
  matrix: 140,
  file_upload: 110,
  rating: 110,
  dropdown: 140,
  radio: 140,
};

const META_COLUMN = {
  _id: { sort: 'reference', w: 120 },
  _date: { sort: 'submitted_at', w: 178 },
  _by: { sort: 'submitter_name', w: 160 },
  _email: { sort: null, w: 200 },
};

export const widthFor = (column) => {
  const base =
    column.type === 'checkbox' ? 180 : WIDTH_BY_TYPE[column.type] || 150;
  // wide enough for the uppercase header, within reason
  const head = Math.min(
    150,
    Math.round(String(column.label || '').length * 7.2 + 34)
  );
  return Math.max(base, head);
};

// The table's column for a key (null when the field is gone).
export const colDef = (key, byId) => {
  if (isMetaKey(key)) {
    return {
      key,
      label: META_LABELS[key],
      meta: true,
      sort: META_COLUMN[key].sort,
      w: META_COLUMN[key].w,
    };
  }
  const column = byId[key];
  if (!column) return null;
  return {
    key,
    label: columnHeader(column),
    column,
    sort: `field:${key}`,
    w: widthFor(column),
    align:
      column.type === 'number' || column.type === 'rating' ? 'center' : null,
  };
};

// field id → its section's label, from the newest definition that has it.
const sectionIndex = (definitions) => {
  const index = {};
  (definitions || []).forEach((definition) => {
    const fields =
      definition && Array.isArray(definition.fields) ? definition.fields : [];
    let section = null;
    fields.forEach((field) => {
      if (!field || !field.id) return;
      if (field.type === 'section') {
        section = String(field.label || '').trim() || 'Section';
        return;
      }
      if (isLayoutType(field.type) || index[field.id] !== undefined) return;
      index[field.id] = section;
    });
  });
  return index;
};

// Every column a view or an export can use, grouped like the form: the
// entry details, then each section's fields, then removed fields.
// definitions: newest first. `exclude` ([key]) drops columns that must
// never be offered (the W-9's TIN and signature in an export, T17).
export const catalogOf = (columns, definitions, { exclude = [] } = {}) => {
  const sections = sectionIndex(definitions);
  const groups = [
    {
      id: '_entry',
      label: 'Entry',
      cols: META_KEYS.map((k) => colDef(k, {})),
    },
  ];
  const byLabel = {};
  const removed = { id: '_removed', label: 'Removed fields', cols: [] };
  const byId = columnsById(columns);
  orderedColumns(columns).forEach((column) => {
    if (exclude.includes(column.field_id)) return;
    const col = colDef(column.field_id, byId);
    if (column.removed) {
      removed.cols.push(col);
      return;
    }
    const label = sections[column.field_id] || 'Form fields';
    if (!byLabel[label]) {
      byLabel[label] = { id: `_s_${label}`, label, cols: [] };
      groups.push(byLabel[label]);
    }
    byLabel[label].cols.push(col);
  });
  if (removed.cols.length) groups.push(removed);
  return groups;
};

export const catalogKeys = (catalog) =>
  catalog.reduce((acc, g) => acc.concat(g.cols.map((c) => c.key)), []);

// The W-9 export catalog: as catalogOf, without the TIN and the signature.
export const w9ExportCatalogOf = (columns, definitions) =>
  catalogOf(columns, definitions, { exclude: W9_EXPORT_EXCLUDED });

const PREFERRED_TYPES = ['name', 'email', 'phone'];
const SKIP_IN_DEFAULT = [
  'long_answer',
  'file_upload',
  'table',
  'matrix',
  'repeater',
];
const MAX_DEFAULT_FIELDS = 4;

// A sensible starting table for any form: the entry ID, who it's from and
// how to reach them, a couple more short answers, and when it came in.
export const defaultCols = (columns) => {
  const current = orderedColumns(columns).filter((c) => !c.removed);
  const picked = [];
  PREFERRED_TYPES.forEach((type) => {
    const column = current.find((c) => c.type === type);
    if (column) picked.push(column.field_id);
  });
  current.forEach((c) => {
    if (
      picked.length < MAX_DEFAULT_FIELDS &&
      !picked.includes(c.field_id) &&
      !SKIP_IN_DEFAULT.includes(c.type)
    ) {
      picked.push(c.field_id);
    }
  });
  const hasName = current.some((c) => c.type === 'name');
  return ['_id', ...(hasName ? [] : ['_by']), ...picked, '_date'];
};

// The W-9's three built-in views (w9-plan §6.3 item 7). Payee fields are
// the current columns outside the system set, in form order.
export const W9_VIEW_IDS = {
  default: DEFAULT_VIEW_ID,
  prep1099: 'w9_1099',
  contact: 'w9_contact',
};

export const w9BuiltInViews = (columns) => {
  const current = orderedColumns(columns).filter((c) => !c.removed);
  const payee = current
    .filter((c) => !isW9SystemId(c.field_id))
    .map((c) => c.field_id);
  const has = (id) => current.some((c) => c.field_id === id);
  const only = (keys) => keys.filter((k) => isMetaKey(k) || has(k));
  return [
    {
      id: W9_VIEW_IDS.default,
      name: 'Default',
      builtIn: true,
      cols: only([
        NAME_FIELD_ID,
        BUSINESS_FIELD_ID,
        ...payee,
        CLASS_FIELD_ID,
        '_date',
      ]),
    },
    {
      id: W9_VIEW_IDS.prep1099,
      name: '1099 prep',
      builtIn: true,
      cols: only([
        NAME_FIELD_ID,
        BUSINESS_FIELD_ID,
        CLASS_FIELD_ID,
        TIN_FIELD_ID,
        ADDRESS_FIELD_ID,
      ]),
    },
    {
      id: W9_VIEW_IDS.contact,
      name: 'Contact sheet',
      builtIn: true,
      cols: only([NAME_FIELD_ID, ...payee]),
    },
  ];
};

// ---- views ---------------------------------------------------------------

const unique = (keys) => keys.filter((k, i) => keys.indexOf(k) === i);

// Drops keys that no longer exist. Until the columns load (no keys yet)
// nothing is dropped.
export const cleanCols = (cols, keys) =>
  unique(
    (cols || []).filter(
      (k) => typeof k === 'string' && (!keys.length || keys.includes(k))
    )
  );

// Stored views → the views to show: always a built-in Default first, every
// view's columns cleaned, and a view left with none falls back to the
// default set. `builtIns` (default: the one Default view) seeds the fixed
// views; a stored copy of a built-in keeps its id and name but may carry
// the person's own columns (the Columns dialog edits them).
export const resolveViews = (stored, keys, defaults, builtIns) => {
  const list = stored && Array.isArray(stored.views) ? stored.views : [];
  const fix = (cols) => {
    const kept = cleanCols(cols, keys);
    return kept.length ? kept : defaults;
  };
  const saved = list.filter(
    (v) => v && typeof v.id === 'string' && Array.isArray(v.cols)
  );
  const fixed =
    Array.isArray(builtIns) && builtIns.length
      ? builtIns
      : [
          {
            id: DEFAULT_VIEW_ID,
            name: 'Default',
            builtIn: true,
            cols: defaults,
          },
        ];
  const fixedIds = fixed.map((v) => v.id);
  const views = [
    ...fixed.map((view) => {
      const storedCopy = saved.find((v) => v.id === view.id);
      return {
        id: view.id,
        name: view.name,
        builtIn: true,
        cols: storedCopy ? fix(storedCopy.cols) : view.cols,
      };
    }),
    ...saved
      .filter((v) => !fixedIds.includes(v.id))
      .map((v) => ({
        id: v.id,
        name: String(v.name || 'View'),
        cols: fix(v.cols),
      })),
  ];
  const active =
    stored && views.some((v) => v.id === stored.active)
      ? stored.active
      : DEFAULT_VIEW_ID;
  return { active, views };
};

export const newViewId = (now = Date.now()) => `v_${now.toString(36)}`;

// ---- storage -------------------------------------------------------------

const storageKey = (kind, formId, userId) =>
  `pp_forms_${kind}_v1_${userId || 'anon'}_${formId}`;

const read = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const write = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Private mode or a full quota: the view still works until reload.
  }
};

export const loadViews = (formId, userId) =>
  read(storageKey('views', formId, userId));

export const saveViews = (formId, userId, state) =>
  write(storageKey('views', formId, userId), {
    active: state.active,
    views: state.views.map(({ id, name, cols }) => ({ id, name, cols })),
  });

export const loadExportChoice = (formId, userId) =>
  read(storageKey('export', formId, userId));

export const saveExportChoice = (formId, userId, choice) =>
  write(storageKey('export', formId, userId), choice);
